import { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Badge, Button } from 'reactstrap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { httpClient } from '@s20/services/httpClient';
import Slideout from '../../common/Slideout';
import { ApiRoutes } from '../../Common';
import {
    FlexColumnCenter, FormCircularProgress, Textarea, toasty,
} from '../../common/forms/FormElements';
import { UncontrolledSaveButton } from '../../common/forms/SaveButton';
import { Modal } from '../../common/Modal';
import { JobRequestStatuses } from './JobRequest';

export const JobRequestSlideout = ({ show, onClose, jobRequestId }) => {
    const queryClient = useQueryClient();

    const { data, isLoading } = useQuery({
        queryKey: ['jobRequest', jobRequestId],
        queryFn: () => httpClient.js(ApiRoutes.jobRequest.byId(jobRequestId)),
        enabled: Boolean(jobRequestId),
    });

    const handleApproveButtonClick = useMutation({
        mutationFn: async () => {
            await httpClient.put(
                ApiRoutes.jobRequest.updateStatus(data.id, JobRequestStatuses.Approved),
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['jobRequest', jobRequestId] });
            toasty.success('Job Request', 'This job request has been approved');
        },
        onError: () => {
            toasty.error('Job Request', 'Failed to update status.');
        },
    });

    const { mutateAsync: handleRejection } = useMutation({
        mutationFn: async (notes) => {
            await httpClient.put(
                ApiRoutes.jobRequest.reject(data.id),
                { notes },
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['jobRequest', jobRequestId] });
            toasty.success('Job Request', 'This job request has been rejected');
        },
        onError: () => {
            toasty.error('Job Request', 'Failed to update status.');
        },
    });

    const handleRejectButtonClick = useCallback(() => {
        Modal.show({
            title: 'Reject Job Request',
            SaveButtonProps: {
                color: 'danger',
                label: 'Yes, reject this request',
            },
            onSubmit: ({ notes }) => handleRejection(notes),
            body: () => (
                <Textarea
                    label="Please enter a rejection reason"
                    name="notes"
                    maxLength={500}
                    required
                    error="A reason for this rejection is required"
                />
            ),
        });
    }, [handleRejection]);

    return createPortal(
        <Slideout
            show={show}
            onClose={onClose}
            isLoading={isLoading}
            formTitle="Job Request"
        >
            {isLoading || !data ? (
                <FlexColumnCenter className="align-items-center p-4">
                    <FormCircularProgress />

                    <span className="h5 mt-3">Loading job request data...</span>
                </FlexColumnCenter>
            ) : (
                <>
                    <dl className="row mb-0">
                        <dt className="col-sm-4">Date Submitted</dt>
                        <dd className="col-sm-8">
                            {moment(data.createdOn).format('YYYY-MM-DD')}
                        </dd>

                        <dt className="col-sm-4">Customer</dt>
                        <dd className="col-sm-8">{data.customerName || '-'}</dd>

                        <dt className="col-sm-4">Contact</dt>
                        <dd className="col-sm-8">{data.contact || '-'}</dd>

                        <dt className="col-sm-4">Description</dt>
                        <dd className="col-sm-8">{data.description || '-'}</dd>

                        <dt className="col-sm-4">Notes</dt>
                        <dd className="col-sm-8">{data.notes || '-'}</dd>

                        <dt className="col-sm-4">Work Order</dt>
                        <dd className="col-sm-8">{data.workOrderNumber || '-'}</dd>

                        <dt className="col-sm-4">Purchase Order</dt>
                        <dd className="col-sm-8">{data.purchaseOrderNumber || '-'}</dd>

                        <dt className="col-sm-4">Meeting Location</dt>
                        <dd className="col-sm-8">{data.meetingLocationAddress || '-'}</dd>

                        <dt className="col-sm-4">Job Location</dt>
                        <dd className="col-sm-8">{data.location || '-'}</dd>

                        <dt className="col-sm-4">Requested Date</dt>
                        <dd className="col-sm-8">
                            {moment(data.requestedDate).format('YYYY-MM-DD')}
                        </dd>

                        <dt className="col-sm-4">Status</dt>
                        <dd className="col-sm-8">
                            <Badge color={data.jobRequestStatusDisplayColor}>
                                {data.jobRequestStatusName}
                            </Badge>
                        </dd>
                    </dl>

                    <footer className="mt-4">
                        <UncontrolledSaveButton
                            size="sm"
                            color="primary"
                            className="mr-2"
                            onClick={handleApproveButtonClick.mutateAsync}
                            label="Approve Request"
                            disabled={
                                data.jobRequestStatusId !== JobRequestStatuses.Submitted
                                && data.jobRequestStatusId !== JobRequestStatuses.Rejected
                            }
                        />

                        <Button
                            size="sm"
                            color="outline-danger"
                            onClick={handleRejectButtonClick}
                            disabled={
                                data.jobRequestStatusId !== JobRequestStatuses.Submitted
                                && data.jobRequestStatusId !== JobRequestStatuses.Approved
                            }
                        >
                            Reject Request
                        </Button>
                    </footer>
                </>
            )}
        </Slideout>,
        document.body,
    );
};